import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import BotInfoCard from '../botInfoCard/BotInfoCard';
import BotUserCard from '../botUserCard/BotUserCard';
import ChatLoader from '../chatLoader/ChatLoader';
import './messageList.scss';
import BotScheduleCard from '../botScheduleCard/BotScheduleCard';
import { CallcentralLogo } from '../../../../components/Shared/CallcentralLogo/CallcentralLogo';
import BotSiteCard from '../botSiteCard/BotSiteCard';
import BotPatientCard from '../botPatientCard/BotPatientCard';
import BotLogsGroup from '../botLogsGroup/BotLogsGroup';
import { IonButton } from '@ionic/react';


interface MessageListProps {
    messages: any[];
    isStreaming: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isStreaming }) => {
    const dispatch = useDispatch();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const prevMessagesLengthRef = useRef<number>(0);
    const prevStreamingStateRef = useRef<boolean>(false);
    const [showAllProfiles, setShowAllProfiles] = useState(false);



    const [isVoiceCallingModalOpen, setIsVoiceCallingModalOpen] = useState<boolean>(false);
    

    // const isStreaming = useAppSelector((state) => state.chatbot.isStreaming);
    // const isStreaming = false

    // Use this effect to scroll when messages change or streaming state changes
    useEffect(() => {
        const messagesChanged = messages.length !== prevMessagesLengthRef.current;
        const streamingStateChanged = isStreaming !== prevStreamingStateRef.current;

        if (messagesEndRef.current && messages.length > 0 && (messagesChanged || streamingStateChanged)) {
            // Store the current values for the next comparison
            prevMessagesLengthRef.current = messages.length;
            prevStreamingStateRef.current = isStreaming;

            // Scroll to bottom with delay options
            const scrollToBottom = () => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            };

            // Immediate scroll attempt
            scrollToBottom();

            // Delayed scroll attempts with various timings
            const timers = [
                setTimeout(scrollToBottom, 100),
                setTimeout(scrollToBottom, 300),
                setTimeout(scrollToBottom, 600)
            ];

            // Clean up timers if component unmounts
            return () => {
                timers.forEach(timer => clearTimeout(timer));
            };
        }
    }, [messages, isStreaming]);

    const toggleVoiceCallingModalOpen = () => setIsVoiceCallingModalOpen(!isVoiceCallingModalOpen);

    const handleFetchContactDetails = (guid: string) => {
        // dispatch(fetchContactDetailsForCall(guid));
        toggleVoiceCallingModalOpen();
    };

    const navigateToAttachMedia = (guid: string, fullName: string, item: any) => {
        console.log('Navigating to attach media', guid);
    };

    const lastLogGroupIndex = (() => {
        for (let i = messages?.length - 1; i >= 0; i--) {
            if (
                messages[i]?.contentType === 'display' &&
                (i === 0 || messages[i - 1].contentType !== 'display')
            ) {
                return i;
            }
        }
        return -1;
    })();

    const renderMessageContent = (item: any) => {
        if (item.isUser) {
            // User message - always text
            return (
                <div className="message-text">{item.text}</div>
            );
        }

        // Bot message - can be different content types
       if (item.contentType === 'user_profile') {
    // Case 1: item.profiles is an array with at least one profile
    if (Array.isArray(item?.profiles) && item?.profiles?.length > 0) {
        const initialProfiles = item.profiles.slice(0, 3);
        const extraProfiles = item.profiles.slice(3);

        return (
            <div className="cards-container">
  {(showAllProfiles ? item.profiles : initialProfiles).map((profile: any, index: number) => (
    <BotUserCard
      key={`${profile?.first_name}-${profile?.last_name}-${index}`}
      imageUrl={profile?.profile_pic || ""}
      isProfilePicSizeSmall
      title={`${profile?.first_name} ${profile?.last_name}`}
      email={profile?.email || ''}
      guid={profile?.guid || ''}
      onPressPhone={() => handleFetchContactDetails(profile?.guid)}
      specialisationNames={profile?.specialisation_names}
      teamNames={profile?.team_names}
    />
  ))}

  {/* Toggle button */}
  {extraProfiles.length > 0 && (
    <IonButton 
      className="more-indicator"
      onClick={() => setShowAllProfiles(prev => !prev)}
    >
      {showAllProfiles ? 'Show less' : `+ ${extraProfiles.length} more`}
    </IonButton>
  )}
</div>
        );
    }

    // Case 2: item.profiles is a string (not an array)
    if (typeof item.profiles === 'string') {
        return <BotInfoCard message={item?.profiles} />;
    }
}

        // Add support for schedule_details content type
        if (item.contentType === 'schedule_details' && item?.schedules) {
            return (
                <div className="schedule-container">
                    <BotScheduleCard schedules={item?.schedules} />
                </div>
            );
        }

        if (item.contentType === 'site') {
            return (
                <BotSiteCard sites={item.sites} />
            );
        }

        if (item.contentType === 'patient_profile') {
            return (
                <BotPatientCard patients={item.patients} />
            );
        }

        if (item.contentType === 'clarification' && item?.clarifications) {
            return (
                <div className="bot-message-text">{item.clarifications?.[0]?.message}</div>
            );
        }

        let messageText = "";

        // if (Array.isArray(item.text)) {
        //     messageText = item?.text?.[0]?.message || '';
        // } else if (typeof item.text === 'string') {
        //     messageText = item.text;
        // }

        if (Array.isArray(item.text)) {
            messageText = item?.text?.[0]?.message || '';
        } else if (typeof item.text === 'string') {
            messageText = item.text;
        }


         if (item.contentType === 'text' && item?.content_data) {
            return (
                <div className="bot-message-text">      
                 {messageText?.split('\n').map((line, index) => (
                    <span key={index}>
                        {line}
                        <br />
                    </span>
                ))}
                </div>
            );
        }

        // Default to text message
        return (
            <div className="bot-message-text">
                {messageText}
            </div>
        );
    };

    const renderMessages = () => {
        return messages.map((item, index) => {
            if (item.isUser) {
                // User message
                return (
                    <div key={item.id || index} className="message-bubble user-bubble">
                        {renderMessageContent(item)}
                    </div>
                );
            }

            //display streaming logs
            if (item.contentType === 'display') {
                if (index === 0 || messages[index - 1].contentType !== 'display') {
                    const hasNonDisplayMessageAfter = messages?.slice(index)?.some(msg => msg?.contentType !== 'display');
                    const shouldStream = index === lastLogGroupIndex && isStreaming && !hasNonDisplayMessageAfter;
                    return <BotLogsGroup messages={messages} startIndex={index} isStreaming={shouldStream && isStreaming} />
                } else {
                    return null;
                }
            }

            // Bot message 
            return (
                <div key={item.id || index} className="bot-message-row">
                    <div className="bot-icon">
                        <img className="svg" src="assets/icon/logoIcon.svg" alt="Ionic logo" />
                    </div>
                    <div className={`message-bubble bot-bubble ${item.contentType === 'user_profile' ? 'bot-profiles-container' : ''}`}>
                        {renderMessageContent(item)}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="message-list-container">
            <div className="message-list">
                {renderMessages()}
                {isStreaming && <ChatLoader isVisible={true} />}
                <div ref={messagesEndRef} />
            </div>

            {/* {isVoiceCallingModalOpen && (
                <VoiceCalling
                    isModalOpen={isVoiceCallingModalOpen}
                    toogleModal={toggleVoiceCallingModalOpen}
                />
            )} */}
        </div>
    );
};

export default MessageList;
